// CLAIM-REPORTS-TAB-V1 (2026-05-18) — Reporting Foundation Arc Part 4.
// REPORT-OUTPUTS-1 (2026-05-19): preview-first workflow.  Available
// cards now open a polished in-browser preview surface (PDF + Excel +
// Print download from there); they no longer trigger a direct XLSX
// download.  Deferred / coming-soon cards unchanged.
//
// Per-claim report hub.  v1 = card grid showing report names, short
// descriptions, available formats, and statuses.
//
// Status values:
//   "available"     — opens preview-first surface (PDF/XLSX/Print)
//   "coming-soon"   — design locked, work scheduled
//   "deferred"      — depends on Phase 4 / future workstream

import { useState } from "react";
import { canOnClaim, getUser } from "../api";
import ClaimSummaryPreview from "./ClaimSummaryPreview";
import PaymentSummaryPreview from "./PaymentSummaryPreview";

// Report card catalog — keep in lockstep with backend report endpoints.
const REPORT_CARDS = [
  {
    id: "claim-summary",
    title: "Claim Summary",
    description: "One-page snapshot — header metadata, lead adjuster, status, dates, financial trio (Total Reserve / Total Payments / Remaining Reserve), policy & coverage, parties.",
    formats: ["PDF", "Excel"],
    status: "available",
    note: "Opens preview; download PDF or Excel from there. Same data as Loss Run for cross-surface parity.",
  },
  {
    id: "payment-summary",
    title: "Payment History",
    description: "Landscape payment ledger — payee, paid-by/received-from, description, check date/no., date of service, 10 category amounts (Medical / Bill Review / Transport / Maint. / Legal / Adjusting / UEW / Settlement / Reimburse / Other Exp.), Total Exp. & Fees, category totals, funding-source split, and the deductible/reimbursement/net-due accounting block.",
    formats: ["PDF", "Excel"],
    status: "available",
    note: "Modeled on the WNW Payment History reference template. Opens preview; download PDF or Excel from there.",
  },
  {
    id: "reserve-summary",
    title: "Reserve Summary",
    description: "Current reserves with the canonical Total Reserve / Total Payments / Remaining Reserve trio.",
    formats: ["XLSX"],
    status: "coming-soon",
    note: "Composes existing /claims/{id}/reserves + shared shell.",
  },
  {
    id: "policy-coverage-summary",
    title: "Policy & Coverage Summary",
    description: "Active policy attachments, coverage lines, slip participation. Same data as the Policy & Coverage tab.",
    formats: ["XLSX"],
    status: "coming-soon",
    note: "Composes existing /claims/{id}/policy-coverage + shared shell.",
  },
  {
    id: "activity-diary-report",
    title: "Activity / Diary Report",
    description: "Reverse-chronological activity feed in printable form. Filterable by event type before export.",
    formats: ["XLSX"],
    status: "coming-soon",
    note: "Composes /claims/{id}/activity + shared shell.",
  },
  {
    id: "document-index",
    title: "Document Index",
    description: "Index of documents linked to this claim with type, filename, uploaded-by, and SharePoint link.",
    formats: ["XLSX"],
    status: "coming-soon",
    note: "Data ready (documents table); ships once Claim Summary lands.",
  },
  {
    id: "full-claim-packet",
    title: "Full Claim Packet",
    description: "Multi-section packet combining the above reports for legal/client distribution.",
    formats: ["PDF (later)"],
    status: "deferred",
    note: "Phase 4. PDF rendering pipeline not in place yet; XLSX-equivalent ships first as a bundled workbook.",
  },
];

const STATUS_META = {
  "available":    { label: "Available",    color: "#2E7D32", bg: "#E8F5E9" },
  "coming-soon":  { label: "Coming Soon",  color: "#B07000", bg: "#FFF8E6" },
  "deferred":     { label: "Deferred",     color: "#666",    bg: "#EEE" },
};

export default function ClaimReportsTab({ claimId, claim }) {
  // null = card grid; "claim-summary" = preview takeover.
  const [openReport, setOpenReport] = useState(null);
  // H17: "Save to Documents" / "Email Report" file a PDF onto the claim — a claim
  // document write, now gated server-side on an edit cap. Mirror that gate here
  // (Invariant #9: server + client, defense in depth) so a read-only role isn't
  // offered a button that must 403. Viewing/downloading the report is unaffected.
  const _u = getUser();
  const canEdit = canOnClaim("edit_claim", claim, _u) || canOnClaim("edit_claim_basic", claim, _u);

  if (openReport === "claim-summary") {
    return <ClaimSummaryPreview claimId={claimId} canEdit={canEdit} onClose={() => setOpenReport(null)} />;
  }
  if (openReport === "payment-summary") {
    return <PaymentSummaryPreview claimId={claimId} canEdit={canEdit} onClose={() => setOpenReport(null)} />;
  }

  function onCardOpen(card) {
    if (card.status !== "available") return;
    setOpenReport(card.id);
  }

  return (
    <div style={{ padding: 8 }}>
      <div className="section-header" style={{ marginBottom: 4 }}>
        <span className="section-title">Claim Reports</span>
      </div>
      <p style={{ fontSize: 13, color: "var(--text2,#555)", marginTop: 4, marginBottom: 16 }}>
        Per-claim reports for this claim. Available cards open a preview;
        download PDF or Excel from there. More cards become Available as
        the shared report foundation rolls out.
      </p>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: 12,
      }}>
        {REPORT_CARDS.map(card => (
          <ReportCard key={card.id} card={card} onOpen={() => onCardOpen(card)} />
        ))}
      </div>
    </div>
  );
}

function ReportCard({ card, onOpen }) {
  const meta = STATUS_META[card.status] || STATUS_META["deferred"];
  const isAvailable = card.status === "available";

  return (
    <div className="card" style={{
      padding: 14,
      display: "flex", flexDirection: "column", gap: 8,
      borderLeft: `4px solid ${meta.color}`,
      opacity: isAvailable ? 1 : 0.92,
    }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text,#1A1A1A)" }}>
          {card.title}
        </div>
        <span style={{
          fontSize: 10, padding: "2px 8px", borderRadius: 10,
          background: meta.bg, color: meta.color, fontWeight: 600,
          textTransform: "uppercase", letterSpacing: 0.5,
          whiteSpace: "nowrap",
        }}>{meta.label}</span>
      </div>

      <div style={{ fontSize: 12, color: "var(--text2,#555)", lineHeight: 1.45, flex: 1 }}>
        {card.description}
      </div>

      {card.note && (
        <div style={{ fontSize: 11, color: "var(--text3,#888)", fontStyle: "italic" }}>
          {card.note}
        </div>
      )}

      <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
        {isAvailable ? (
          <button
            onClick={onOpen}
            title="Open preview"
            style={{
              padding: "6px 14px", fontSize: 12,
              background: "var(--navy,#1F3864)",
              color: "white",
              border: "none", borderRadius: 4,
              cursor: "pointer", fontWeight: 600,
            }}>
            Open Preview
          </button>
        ) : (
          card.formats.map(fmt => (
            <span key={fmt} title="Not available yet"
                  style={{
                    padding: "5px 12px", fontSize: 12,
                    background: "var(--border-light,#EEF1F4)",
                    color: "var(--text3,#888)",
                    borderRadius: 4, fontWeight: 600,
                    cursor: "not-allowed",
                  }}>
              {fmt}
            </span>
          ))
        )}
      </div>
    </div>
  );
}
